import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationsResponse } from '@lista/shared-validation';
import { useCore } from '../context';
import { queryKeys } from '../query-keys';

/** How often the open app asks for new notifications (it also refreshes on focus). */
export const NOTIFICATIONS_POLL_MS = 15_000;

/** My notifications, newest first, with the unread total. Polls while the app is open. */
export function useNotifications() {
  const { services } = useCore();
  return useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => services.notifications.list(),
    refetchInterval: NOTIFICATIONS_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** Number for the bell badge. */
export function useUnreadNotificationCount(): number {
  return useNotifications().data?.unreadCount ?? 0;
}

function useNotificationsCache() {
  const qc = useQueryClient();
  return {
    patch(update: (n: NotificationsResponse) => NotificationsResponse) {
      const before = qc.getQueryData<NotificationsResponse>(queryKeys.notifications);
      qc.setQueryData<NotificationsResponse>(queryKeys.notifications, (n) => (n ? update(n) : n));
      return { before };
    },
    restore(before: NotificationsResponse | undefined) {
      qc.setQueryData(queryKeys.notifications, before);
    },
    refresh: () => void qc.invalidateQueries({ queryKey: queryKeys.notifications }),
  };
}

export function useMarkNotificationRead() {
  const { services } = useCore();
  const cache = useNotificationsCache();
  return useMutation({
    mutationFn: (id: string) => services.notifications.markRead(id),
    onMutate: (id) =>
      cache.patch((n) => {
        const target = n.data.find((x) => x.id === id);
        const wasUnread = !!target && target.readAt === null;
        return {
          ...n,
          unreadCount: Math.max(0, n.unreadCount - (wasUnread ? 1 : 0)),
          data: n.data.map((x) =>
            x.id === id && x.readAt === null ? { ...x, readAt: new Date().toISOString() } : x,
          ),
        };
      }),
    onError: (_e, _v, ctx) => cache.restore(ctx?.before),
    onSettled: cache.refresh,
  });
}

export function useMarkAllNotificationsRead() {
  const { services } = useCore();
  const cache = useNotificationsCache();
  return useMutation({
    mutationFn: () => services.notifications.markAllRead(),
    onMutate: () =>
      cache.patch((n) => ({
        ...n,
        unreadCount: 0,
        data: n.data.map((x) => (x.readAt ? x : { ...x, readAt: new Date().toISOString() })),
      })),
    onError: (_e, _v, ctx) => cache.restore(ctx?.before),
    onSettled: cache.refresh,
  });
}
